// Copyright 2023-2026 - Hewlett Packard Enterprise Company

export const PAGE_LIMIT = 1000;

export const URLS = {};

export const COUNTS = {
  MAX_DOORWAY_COUNT: 800,
  NUM_ITEMS_IN_ACTIVITY_CARD: 5,
  MAX_COBALT_COUNT: 100,
  MAX_IRIS_COUNT: 100,
  MAX_IRIS_INVENTORY_COUNT: 50,
  MAX_APPLIANCE_FW_BUNDLE_COUNT: 100,
  MAX_FILTER_GET_ALL_SERVERS: 10,
  MAX_FILTER_GET_ALL_APPLIANCES: 100,
  MAX_FILTER_LENGTH_GET_ALL_SERVERS: 1995,
  MAX_GET_SERVERS_ANALYTICS_COUNT: 100,
  MAX_GET_TAGS_V1_COUNT: 100,
};

export const setApiUrls = (
  COM_API_ROOT_URL,
  DOORWAY_PATH = '/ui-doorway/compute',
  COBALT_PATH = '/api/compute',
  COMPUTE_OPS_PATH = '/compute-ops',
  COMPUTE_OPS_MGMT_PATH = '/compute-ops-mgmt',
  VERSION_OVERRIDES = {},
) => {
  const v1 = VERSION_OVERRIDES.v1 || 'v1';
  const v2 = VERSION_OVERRIDES.v2 || 'v2';
  const v1beta1 = VERSION_OVERRIDES.v1beta1 || 'v1beta1';
  const v1beta2 = VERSION_OVERRIDES.v1beta2 || 'v1beta2';
  const v1beta3 = VERSION_OVERRIDES.v1beta3 || 'v1beta3';

  // If the doorway path is set to compute-ops-mgmt, that means
  // APIs are configured to use the compute-ops-mgmt URL for doorway calls,
  // so we need to set the max doorway count to 100 instead of 800.
  // This is because the compute-ops-mgmt APIs has max limit of 100,
  // while the ui-doorway APIs has max limit of 800.
  COUNTS.MAX_DOORWAY_COUNT = DOORWAY_PATH === COMPUTE_OPS_MGMT_PATH ? 100 : 800;

  URLS.COM_API_ROOT_URL = COM_API_ROOT_URL;
  URLS.WEBSOCKET_TICKET_URL = `${COM_API_ROOT_URL}/api/ui/ticket`;

  URLS.DOORWAY_URL = `${COM_API_ROOT_URL}${DOORWAY_PATH}`;
  URLS.COBALT_URL = `${COM_API_ROOT_URL}${COBALT_PATH}`;
  URLS.COMPUTE_OPS_URL = `${COM_API_ROOT_URL}${COMPUTE_OPS_PATH}`;
  URLS.COMPUTE_OPS_MGMT_URL = `${COM_API_ROOT_URL}${COMPUTE_OPS_MGMT_PATH}`;
  URLS.CHAT_URL = `${URLS.DOORWAY_URL}/${v1}/chats`;

  URLS.ILOS_URL = `${URLS.COBALT_URL}/${v1}/servers`;
  URLS.JOBS_URL = `${URLS.COBALT_URL}/${v1}/jobs`;
  URLS.BIOS_SETTINGS_URL = `${URLS.COBALT_URL}/${v1}/device-settings`;
  URLS.FIRMWARE_CA_REPORTS_URL = `${URLS.COBALT_URL}/${v1}/firmware-ca-reports`;
  URLS.FIRMWARE_BUNDLES_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/firmware-bundles`;
  URLS.FIRMWARE_BUNDLES_URL = `${URLS.DOORWAY_URL}/${v1}/firmware-bundles`;
  URLS.COMPONENT_PACKAGES_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/component-packages`;
  URLS.FW_PREFLIGHT_URL = `${URLS.COBALT_URL}/${v1}/analyze-firmware-update`;
  URLS.OPS_MGMT_OS_PREFLIGHT_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/servers`;

  URLS.COMPUTE_OPS_MGMT_SCHEDULE_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/schedules`;
  URLS.COMPUTE_OPS_MGMT_JOBS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/jobs`;
  URLS.COMPUTE_OPS_MGMT_JOBS_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/jobs`;
  URLS.COMPUTE_OPS_MGMT_REPORTS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/reports`;

  URLS.OPS_MGMT_SAVED_FILTERS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/filters`;
  URLS.OPS_MGMT_EMAIL_NOTIFICATION_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/user-preferences`;
  URLS.OPS_MGMT_EMAIL_NOTIFICATION_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/user-preferences`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SERVER_NOTIFICATIONS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/server-notifications`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SERVER_NOTIFICATIONS_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/server-notifications`;
  URLS.METRICS_DATA_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/metrics-configurations`;
  URLS.SERVERS_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_URL}/${v1beta2}/servers`;
  URLS.OPS_MGMT_SERVERS_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/servers`;
  URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/servers`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL_BULK = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/servers/bulk`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SERVERS_V1_POST_PATCH_DEL_URL_BULK_DELETE = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/servers/bulk-delete`;
  URLS.OPS_MGMT_GROUPS_V1B2_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/groups`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_GROUPS_V1_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/groups`;
  // eslint-disable-next-line max-len
  URLS.ENERGY_OVER_TIME = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/energy-over-time`;
  URLS.ENERGY_BY_ENTITY_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/energy-by-entity`;
  URLS.OPS_MGMT_APPROVAL_POLICY_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/approval-policies`;
  URLS.ACTIVITIES_ASSOCIATED_SERVER_PREFIX = `${COMPUTE_OPS_MGMT_PATH}/${v1beta2}/servers`;
  // eslint-disable-next-line max-len
  URLS.APPROVABLES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/approval-policies/approvables`;
  // eslint-disable-next-line max-len
  URLS.APPROVAL_POLICY_CHECK_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/approval-policies/check`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_APPROVAL_POLICIES_V1B2_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/approval-policies`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_APPROVAL_REQUESTS_V1B2_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/approval-requests`;
  URLS.OPS_MGMT_DISCOVERED_SERVERS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/discovered-servers`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_DISCOVERED_SERVERS_LAST_COLLECTION_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/discovered-servers/summary`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_GATEWAY_DNS_NTP_DATA_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/appliances/{device_id}/config`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_ONBOARD_SERVERS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/discovered-servers/actions/onboard`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SECURE_HARDWARE_GROUPS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/groups`;

  // Doorway API endpoints
  // eslint-disable-next-line max-len
  URLS.UTILIZATION_AVERAGE_OVER_TIME = `${URLS.DOORWAY_URL}/${v1}/utilization-averages`;
  URLS.UTILIZATION_BY_ENTITY_URL = `${URLS.DOORWAY_URL}/${v1}/utilization-by-entity`;
  URLS.SERVERS_DATA_URL = `${URLS.DOORWAY_URL}/${v2}/servers/data`;
  URLS.FIRMWARE_UPGRADES_URL = `${URLS.DOORWAY_URL}/${v2}/firmware-upgrades`;
  URLS.FIRMWARE_ANALYSIS_URL = `${URLS.DOORWAY_URL}/${v2}/firmware-analysis`;
  URLS.METRICS_DATA_RANGE = `${URLS.DOORWAY_URL}/${v1}/metrics-data-range`;
  URLS.SERVERS_URL = `${URLS.DOORWAY_URL}/${v2}/servers`;
  URLS.SERVERS_COUNTS_URL = `${URLS.DOORWAY_URL}/${v2}/servers/counts`;
  URLS.SERVERS_URL_V1 = `${URLS.DOORWAY_URL}/${v1}/servers`;
  URLS.SERVERS_OVERVIEW_URL = `${URLS.DOORWAY_URL}/${v1}/servers-overview`;
  URLS.SUPPORT_CASE_URL = `${URLS.DOORWAY_URL}/${v1}/support-case`;
  URLS.MANUAL_SUPPORT_CASE_URL = `${URLS.DOORWAY_URL}/${v1}/manual-support-case`;
  URLS.SUPPORT_CASES_URL = `${URLS.DOORWAY_URL}/${v1}/support-cases`;
  URLS.CUSTOMERS_URL = `${URLS.DOORWAY_URL}/${v1}/accounts`;
  URLS.UI_DOORWAY_ACTIVITIES_URL = `${URLS.DOORWAY_URL}/${v1}/activities`;
  URLS.SBAC_CHECK_URL = `${URLS.DOORWAY_URL}/${v1}/user-accessible-resources`;
  URLS.USER_PERMISSIONS_URL = `${URLS.DOORWAY_URL}/${v1}/user-permissions`;
  URLS.SERVER_INSIGHTS_URL = `${URLS.DOORWAY_URL}/${v1}/insights`;
  URLS.SERVER_INSIGHTS_V2_URL = `${URLS.DOORWAY_URL}/${v2}/insights`;
  URLS.USERS_URL = `${URLS.DOORWAY_URL}/${v1}/users-with-permission`;
  URLS.WORKSPACE_USERS_URL = `${URLS.DOORWAY_URL}/${v1}/all-users`;
  URLS.ENERGY_DATA_EXPORT_URL = `${URLS.DOORWAY_URL}/${v1}/energy-data-export`;
  URLS.EXTERNAL_SERVICE_EXPORT_URL = `${URLS.DOORWAY_URL}/${v2}/external-services`;
  URLS.UTILIZATION_DATA_EXPORT_URL = `${URLS.DOORWAY_URL}/${v1}/utilization-data-export`;
  URLS.APPROVAL_POLICY_URL = `${URLS.DOORWAY_URL}/${v1}/approval-policies`;
  URLS.APPROVAL_REQUESTS_URL = `${URLS.DOORWAY_URL}/${v1}/approval-requests`;
  // eslint-disable-next-line max-len
  URLS.APPROVAL_REQUESTS_COMPUTE_OPS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/approval-requests`;
  // eslint-disable-next-line max-len
  URLS.NOTIFICATION_PREFERENCES_SUMMARY_URL = `${URLS.DOORWAY_URL}/${v2}/notification-preferences/summary`;

  // OV API endpoints
  URLS.OPS_MGMT_OV_APPLIANCES_URI = `/compute-ops-mgmt/${v1beta1}/oneview-appliances`;
  URLS.OPS_MGMT_GATEWAY_APPLIANCE_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/appliances`;
  URLS.OPS_MGMT_OV_APPLIANCES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-appliances`;
  URLS.OPS_MGMT_OV_RESOURCES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-resources`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_OV_SERVER_HW_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-server-hardware`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_OV_SERVER_PROFILE_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-server-profiles`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_APPLIANCE_SETTINGS_TEMPLATE_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-settings-templates`;
  // eslint-disable-next-line max-len
  URLS.APPLIANCE_SETTINGS_COUNTS_URL = `${URLS.DOORWAY_URL}/${v1}/oneview-settings-templates/counts`;
  URLS.OV_APPLIANCE_SETTINGS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-settings`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_OV_SERVER_TEMPLATES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/oneview-server-templates`;
  // eslint-disable-next-line max-len
  URLS.APPLIANCE_FIRMWARE_BUNDLES_URL_V1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/appliance-firmware-bundles`;
  // eslint-disable-next-line max-len
  URLS.APPLIANCE_FW_BUNDLE_UIDOORWAY_URL = `${URLS.DOORWAY_URL}/${v1}/appliance-firmware-bundles`;
  URLS.APPLIANCES_UIDOORWAY_URL = `${URLS.DOORWAY_URL}/${v2}/appliances`;
  URLS.APPLIANCES_COUNTS_UIDOORWAY_URL = `${URLS.APPLIANCES_UIDOORWAY_URL}/counts`;

  URLS.OPS_MGMT_OV_HARDWARE_COUNTS_URL = `${URLS.OPS_MGMT_OV_RESOURCES_URL}/counts`;
  URLS.OPS_MGMT_OV_SERVER_HW_COUNTS_URL = `${URLS.OPS_MGMT_OV_SERVER_HW_URL}/counts`;
  URLS.OPS_MGMT_OV_PROFILE_COUNTS_URL = `${URLS.OPS_MGMT_OV_RESOURCES_URL}/counts`;
  URLS.OPS_MGMT_OV_SERVER_PROFILE_COUNTS_URL = `${URLS.OPS_MGMT_OV_SERVER_PROFILE_URL}/counts`;

  URLS.GROUPS_URL_V2 = `${URLS.DOORWAY_URL}/${v2}/groups`;
  URLS.GROUP_COUNTS_URL = `${URLS.DOORWAY_URL}/${v2}/groups/counts`;
  URLS.OPS_MGMT_GROUP_POST_PATCH_DEL_V1_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/groups`;

  URLS.SERVER_SETTINGS_URL = `${URLS.DOORWAY_URL}/${v2}/server-settings`;
  URLS.OPS_MGMT_SETTINGS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/settings`;
  // eslint-disable-next-line max-len
  URLS.OPS_MGMT_SRVR_SETTINGS_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/server-settings`;
  URLS.OPS_MGMT_SETTINGS_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/settings`;
  URLS.OPS_MGMT_SETTINGS_V1_POST_PATCH_DEL_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/settings`;

  URLS.JOBS_V1B3_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta3}/jobs`;
  URLS.OPS_MGMT_JOBS_V1B3_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta3}/jobs`;

  URLS.OPS_MGMT_SETTING_URI_PREFIX = `/compute-ops-mgmt/${v1beta1}/settings`;
  URLS.OPS_MGMT_SETTING_V1_URI_PREFIX = `/compute-ops-mgmt/${v1}/settings`;
  URLS.OPS_MGMT_SERVERS_V1_URI_PREFIX = `/compute-ops-mgmt/${v1}/servers`;
  URLS.OPS_MGMT_GROUPS_V1_URI_PREFIX = `/compute-ops-mgmt/${v1}/groups`;
  URLS.OPS_MGMT_APPROVAL_POLICIES_V1_URI_PREFIX = `/compute-ops-mgmt/${v1beta1}/approval-policies`;

  URLS.TAGS_URL = `${URLS.DOORWAY_URL}/${v2}/tags`;
  URLS.GLP_TAGS_URL = `${URLS.DOORWAY_URL}/${v2}/glp-tags`;
  URLS.TAG_NAMES_URL = `${URLS.DOORWAY_URL}/${v1}/tag-names`;

  URLS.GLOBAL_PREFERENCES_URL = `${URLS.DOORWAY_URL}/${v1}/global-preferences`;
  URLS.PROVISIONED_REGIONS_URL = `${URLS.DOORWAY_URL}/${v1}/provisioned-regions`;
  URLS.MSP_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/accounts`;

  URLS.SDI_URL = `${URLS.COBALT_URL}/${v1}/supportinfo`;
  URLS.SDI_CONFIG_URL = `${URLS.SDI_URL}/servers`;
  URLS.SDI_LOCATIONS_URL = `${URLS.SDI_URL}/locations`;

  URLS.OPS_MGMT_EXTERNAL_SERVICE_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/external-services`;
  URLS.SERVER_ACTIVATION_TOKEN_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/activation-tokens`;
  URLS.ACTIVATION_KEYS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/activation-keys`;
  URLS.ACTIVATION_KEYS_DATA_URL = `${URLS.DOORWAY_URL}/${v1}/activation-keys`;
  // eslint-disable-next-line max-len
  URLS.ACTIVATION_KEYS_ACCESS_PROFILES_URL = `${URLS.DOORWAY_URL}/${v1}/activation-keys/access-profiles`;

  URLS.AHS_FILES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/ahs-files`;
  URLS.AHS_SERVERS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/ahs-servers`;
  URLS.SUPPORT_AHS_FILES_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/support-ahs-files`;
  URLS.FILE_UPLOAD_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/file-upload`;

  URLS.SUBSCRIPTION_URL = `${URLS.DOORWAY_URL}/${v1}/subscriptions`;
  URLS.SUBSCRIPTION_POOL_URL = `${URLS.DOORWAY_URL}/${v1}/subscription-pool`;
  URLS.ONEVIEW_SUBSCRIPTIONS_URL = `${URLS.DOORWAY_URL}/${v1}/oneview-subscriptions`;
  URLS.GATEWAY_APPLIANCE_URL = `/compute-ops-mgmt/${v1beta1}/appliances/gateway`;
  URLS.SUBSCRIPTION_KEY_REGISTRATION_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/claim`;
  URLS.SERVER_LOCATIONS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/server-locations`;
  URLS.APPLIANCES_URL = `${URLS.DOORWAY_URL}/${v1}/appliances`;
  URLS.LOCATIONS_URL = `${URLS.DOORWAY_URL}/${v1}/locations`;
  URLS.ALL_USERS_URL = `${URLS.DOORWAY_URL}/${v1}/all-users`;
  URLS.ALERTS_URL_V1BETA1 = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/alerts`;

  URLS.SECURE_GATEWAY_DOWNLOAD_URL = `${URLS.DOORWAY_URL}/${v1}/gateway/downloads`;

  URLS.REPORT_METADATA_DOORWAY_V2_URL = `${URLS.DOORWAY_URL}/${v2}/reports`;
  URLS.COMPUTE_OPS_MGMT_REPORT_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta2}/reports`;
  // eslint-disable-next-line max-len
  URLS.COMPUTE_OPS_MGMT_SI_REPORT_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/server-hardware-inventory-report`;
  // eslint-disable-next-line max-len
  URLS.SI_REPORT_EXPORT_URL = `${URLS.DOORWAY_URL}/${v1}/server-hardware-inventory-report/export-data`;
  // eslint-disable-next-line max-len
  URLS.METRICS_COLLECTION_REQUESTS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1beta1}/metrics-collection-requests`;
  URLS.ASYNC_OPERATIONS_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/${v1}/async-operations`;

  // Auth APIs always use v1beta1 regardless of VERSION_OVERRIDES
  URLS.ONPREM_LOGIN_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/v1beta1/auth/login`;
  URLS.ONPREM_LOGOUT_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/v1beta1/auth/logout`;
  URLS.ONPREM_REFRESH_URL = `${URLS.COMPUTE_OPS_MGMT_URL}/v1beta1/auth/refresh-token`;
};
